// src/components/schedule/ScheduleBuilder.js
import { useState, useEffect } from "react";
import { buildCron, parseCron, describeCron } from "./scheduleCron";

const FREQS = [
  { v: "hourly", label: "Hourly" },
  { v: "daily", label: "Daily" },
  { v: "weekly", label: "Weekly" },
  { v: "monthly", label: "Monthly" },
  { v: "advanced", label: "Advanced (cron)" },
];
const DOW = [["Sun", 0], ["Mon", 1], ["Tue", 2], ["Wed", 3], ["Thu", 4], ["Fri", 5], ["Sat", 6]];

export default function ScheduleBuilder({ value, onChange }) {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [state, setState] = useState(() => {
    const parsed = parseCron(value);
    return { time: "09:00", everyHours: 6, daysOfWeek: [1], dayOfMonth: 1, timezone: tz, ...parsed };
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onChange(buildCron(state)); }, [state]);

  const set = (patch) => setState((s) => ({ ...s, ...patch }));
  const toggleDay = (d) =>
    setState((s) => {
      const has = (s.daysOfWeek || []).includes(d);
      return { ...s, daysOfWeek: has ? s.daysOfWeek.filter((x) => x !== d) : [...(s.daysOfWeek || []), d] };
    });

  const inputCls = "text-sm border border-input rounded-lg px-3 py-2 bg-background focus:ring-primary/20 focus:border-primary";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {FREQS.map((f) => (
          <button key={f.v} type="button" onClick={() => set({ frequency: f.v })}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              state.frequency === f.v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {state.frequency === "hourly" && (
        <label className="flex items-center gap-2 text-sm text-foreground">
          Every
          <input type="number" min="1" max="23" value={state.everyHours}
            onChange={(e) => set({ everyHours: parseInt(e.target.value, 10) || 1 })} className={`${inputCls} w-20`} />
          hours
        </label>
      )}

      {(state.frequency === "daily" || state.frequency === "weekly" || state.frequency === "monthly") && (
        <div className="flex flex-wrap items-center gap-3">
          {state.frequency === "monthly" && (
            <label className="flex items-center gap-2 text-sm text-foreground">
              On day
              <input type="number" min="1" max="28" value={state.dayOfMonth}
                onChange={(e) => set({ dayOfMonth: parseInt(e.target.value, 10) || 1 })} className={`${inputCls} w-20`} />
            </label>
          )}
          <label className="flex items-center gap-2 text-sm text-foreground">
            at
            <input type="time" value={state.time} onChange={(e) => set({ time: e.target.value })} className={inputCls} />
          </label>
          <span className="text-xs text-muted-foreground">{state.timezone}</span>
        </div>
      )}

      {state.frequency === "weekly" && (
        <div className="flex flex-wrap gap-1.5">
          {DOW.map(([label, d]) => (
            <button key={d} type="button" onClick={() => toggleDay(d)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                (state.daysOfWeek || []).includes(d) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {state.frequency === "advanced" && (
        <input type="text" value={state.raw || ""} placeholder="*/5 * * * *"
          onChange={(e) => set({ raw: e.target.value })} className={`${inputCls} w-full font-mono`} />
      )}

      <p className="text-xs text-muted-foreground">
        {describeCron(state)} <span className="font-mono">· {buildCron(state) || "—"}</span>
      </p>
    </div>
  );
}
